import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * Reciprocal-to-the-member banner: when a primary merchant is already
 * approved but hasn't subscribed yet, surface a one-click CTA to activate
 * member benefits (wallet, certificate, referrals, member-pricing savings).
 *
 * If the merchant is already an active subscriber, show a quieter "Switch
 * to Member Mode" pill so they can flip into the member dashboard.
 */
export function ActivateMemberBenefitsBanner({ user, api }) {
  // Only show on primary-merchant accounts (not member-applicants).
  if (user?.role !== 'merchant') return null;

  const isActive = user?.subscription_status === 'active';

  if (!isActive) {
    return (
      <Link
        to="/pricing"
        className="mb-6 flex items-start gap-3 rounded-xl border border-[#00E5FF]/25 bg-gradient-to-r from-[#00E5FF]/10 to-transparent p-4 hover:border-[#00E5FF]/60 transition-colors"
        data-testid="activate-member-benefits-cta"
      >
        <Sparkles className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-white font-medium">Activate your Member benefits</p>
          <p className="text-sm text-[#A0B4CC] mt-0.5">
            Subscribe to any AGA tier to unlock the wallet, your stock certificate, referral commissions, and member-pricing savings — all on this same account.
          </p>
        </div>
        <span className="text-[#00E5FF] text-xs inline-flex items-center gap-1 whitespace-nowrap">Subscribe <ArrowRight className="w-3.5 h-3.5" /></span>
      </Link>
    );
  }

  return (
    <div
      className="mb-6 flex items-start gap-3 rounded-xl border border-[#00E676]/30 bg-gradient-to-r from-[#00E676]/10 to-transparent p-4"
      data-testid="member-mode-banner"
    >
      <CheckCircle2 className="w-5 h-5 text-[#00E676] mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-white font-medium">Member benefits active</p>
        <p className="text-sm text-[#A0B4CC] mt-0.5">
          You also have an active membership. Switch to Member Mode to manage your wallet, referrals, and stock certificate.
        </p>
      </div>
      <Button
        size="sm"
        className="bg-[#00E676] text-[#001627] hover:bg-[#00C853]"
        data-testid="merchant-switch-to-member-btn"
        onClick={async () => {
          try {
            await api.post('/api/users/switch-view', { view: 'member' });
            window.location.href = '/dashboard';
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to switch view');
          }
        }}
      >
        Member Mode <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </div>
  );
}
